import { jsx as _jsx } from "@emotion/react/jsx-runtime";
import { useState } from 'react';
import { css } from '@emotion/react';
import { getCookie } from '@guardian/libs';
import { getBanner, getPuzzlesBanner, } from '@guardian/support-dotcom-components';
import { useIsInView } from '../../lib/useIsInView';
import { shouldHideSupportMessaging, withinLocalNoBannerCachePeriod, setLocalNoBannerCachePeriod, MODULES_VERSION, hasOptedOutOfArticleCount, lazyFetchEmailWithTimeout, } from '../../lib/contributions';
import { submitComponentEvent } from '../../browser/ophan/ophan';
import { getZIndex } from '../../lib/getZIndex';
import { trackNonClickInteraction } from '../../browser/ga/ga';
import { setAutomat } from '../../lib/setAutomat';
import { useOnce } from '../../lib/useOnce';
const buildPayload = async ({ isSignedIn, shouldHideReaderRevenue, isPaidContent, alreadyVisitedCount, engagementBannerLastClosedAt, subscriptionBannerLastClosedAt, countryCode, optedOutOfArticleCount, asyncArticleCount, sectionId, tags, }) => {
    return {
        tracking: {
            ophanPageId: window.guardian.config.ophan.pageViewId,
            platformId: 'GUARDIAN_WEB',
            clientName: 'dcr',
            referrerUrl: window.location.origin + window.location.pathname,
        },
        targeting: {
            alreadyVisitedCount,
            shouldHideReaderRevenue,
            isPaidContent,
            showSupportMessaging: !shouldHideSupportMessaging(isSignedIn),
            engagementBannerLastClosedAt,
            subscriptionBannerLastClosedAt,
            mvtId: Number(getCookie({ name: 'GU_mvt_id', shouldMemoize: true })),
            countryCode,
            weeklyArticleHistory: await asyncArticleCount,
            hasOptedOutOfArticleCount: optedOutOfArticleCount,
            modulesVersion: MODULES_VERSION,
            sectionId,
            tagIds: tags.map((tag) => tag.id),
        },
    };
};
export const canShowRRBanner = async ({ remoteBannerConfig, isSignedIn, asyncCountryCode, contentType, sectionId, shouldHideReaderRevenue, isMinuteArticle, isPaidContent, isSensitive, tags, contributionsServiceUrl, alreadyVisitedCount, engagementBannerLastClosedAt, subscriptionBannerLastClosedAt, isPreview, idApiUrl, signInGateWillShow, asyncArticleCount, }) => {
    if (!remoteBannerConfig)
        return { show: false };
    if (shouldHideReaderRevenue ||
        isPaidContent ||
        isPreview ||
        signInGateWillShow) {
        // We never serve Reader Revenue banners in this case
        return { show: false };
    }
    if (engagementBannerLastClosedAt &&
        subscriptionBannerLastClosedAt &&
        withinLocalNoBannerCachePeriod()) {
        return { show: false };
    }
    const countryCode = await asyncCountryCode;
    const optedOutOfArticleCount = await hasOptedOutOfArticleCount();
    const bannerPayload = await buildPayload({
        isSignedIn,
        countryCode,
        contentType,
        sectionId,
        shouldHideReaderRevenue,
        isMinuteArticle,
        isPaidContent,
        tags,
        contributionsServiceUrl,
        isSensitive,
        alreadyVisitedCount,
        engagementBannerLastClosedAt,
        subscriptionBannerLastClosedAt,
        optedOutOfArticleCount,
        asyncArticleCount,
    });
    const response = await getBanner(contributionsServiceUrl, bannerPayload);
    if (!response.data) {
        if (engagementBannerLastClosedAt && subscriptionBannerLastClosedAt) {
            setLocalNoBannerCachePeriod();
        }
        return { show: false };
    }
    const { module, meta } = response.data;
    const fetchEmail = isSignedIn
        ? lazyFetchEmailWithTimeout(idApiUrl)
        : undefined;
    return { show: true, meta: { module, meta, fetchEmail } };
};
export const canShowPuzzlesBanner = async ({ remoteBannerConfig, isSignedIn, asyncCountryCode, contentType, sectionId, shouldHideReaderRevenue, isMinuteArticle, isPaidContent, isSensitive, tags, contributionsServiceUrl, alreadyVisitedCount, engagementBannerLastClosedAt, subscriptionBannerLastClosedAt, section, asyncArticleCount, }) => {
    const isPuzzlesPage = section === 'crosswords' ||
        tags.some((tag) => tag.type === 'Series' && tag.title === 'Sudoku');
    if (shouldHideReaderRevenue) {
        // We never serve Reader Revenue banners in this case
        return { show: false };
    }
    if (isPuzzlesPage && remoteBannerConfig) {
        const countryCode = await asyncCountryCode;
        const optedOutOfArticleCount = await hasOptedOutOfArticleCount();
        const bannerPayload = await buildPayload({
            isSignedIn,
            countryCode,
            contentType,
            sectionId,
            shouldHideReaderRevenue,
            isMinuteArticle,
            isPaidContent,
            tags,
            contributionsServiceUrl,
            isSensitive,
            alreadyVisitedCount,
            engagementBannerLastClosedAt,
            subscriptionBannerLastClosedAt,
            optedOutOfArticleCount,
            asyncArticleCount,
        });
        return getPuzzlesBanner(contributionsServiceUrl, bannerPayload).then((response) => {
            if (!response.data) {
                return { show: false };
            }
            const { module, meta } = response.data;
            return { show: true, meta: { module, meta } };
        });
    }
    return { show: false };
};
const RemoteBanner = ({ componentTypeName, displayEvent, meta, module, fetchEmail, }) => {
    const [Banner, setBanner] = useState();
    const [hasBeenSeen, setNode] = useIsInView({
        threshold: 0,
        debounce: true,
    });
    useOnce(() => {
        if (module === undefined || meta === undefined) {
            return;
        }
        setAutomat();
        window
            .guardianPolyfilledImport(module.url)
            .then((bannerModule) => {
            setBanner(() => bannerModule[module.name]); // useState requires functions to be wrapped
        })
            .catch((error) => {
            const msg = `Error importing RR banner: ${error}`;
            // eslint-disable-next-line no-console
            console.log(msg);
            window.guardian.modules.sentry.reportError(new Error(msg), 'rr-banner');
        });
    }, []);
    useOnce(() => {
        const { componentType } = meta;
        // track banner view event in Google Analytics for subscriptions banner
        if (componentType === componentTypeName) {
            trackNonClickInteraction(displayEvent);
        }
    }, [hasBeenSeen, meta]);
    if (Banner) {
        return (
        // The css here is necessary to put the container div in view, so that we can track the view
        _jsx("div", Object.assign({ ref: setNode, css: css `
					width: 100%;
					${getZIndex('banner')}
				` }, { children: _jsx(Banner, Object.assign({}, module.props, { 
                // @ts-ignore
                submitComponentEvent: submitComponentEvent, fetchEmail: fetchEmail }), void 0) }), void 0));
    }
    return null;
};
export const ReaderRevenueBanner = ({ meta, module, fetchEmail, }) => (_jsx(RemoteBanner, { componentTypeName: "ACQUISITIONS_SUBSCRIPTIONS_BANNER", displayEvent: "subscription-banner : display", meta: meta, module: module, fetchEmail: fetchEmail }, void 0));
export const PuzzlesBanner = ({ meta, module }) => (_jsx(RemoteBanner, { componentTypeName: "ACQUISITIONS_OTHER", displayEvent: "puzzles-banner : display", meta: meta, module: module }, void 0));
